import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import {
  useSelector,
} from 'react-redux';

import type {
  RootState,
} from '../../../app/store';

import {
  ArrowLeft,
  CalendarDays,
  Coins,
  Gift,
  History,
  Pencil,
  Plus,
  Sparkles,
  Ticket,
  Trophy,
  UserRound,
  WalletCards,
  X,
} from 'lucide-react';

import {
  memberService,
} from '../services/memberService';

import type {
  MemberInfo,
} from '../types/member.types';

import {
  storeService,
} from '../../store/services/storeService';

import type {
  StoreListItem,
} from '../../store/types/store.types';

import {
  seedService,
} from '../../seed/services/seedService';

import type {
  StoreUserPoint,
} from '../../seed/types/seed.types';

import {
  rankingService,
} from '../../ranking/services/rankingService';

import type {
  StoreUserScore,
} from '../../ranking/types/ranking.types';

import {
  historyService,
} from '../../history/services/historyService';

import type {
  HistoryItem,
} from '../../history/types/history.types';

import {
  adminCouponService,
} from '../../coupon/services/adminCouponService';

import type {
  AdminUserCoupon,
  Coupon,
} from '../../coupon/types/coupon.admin.types';

import type {
  CouponRewardType,
} from '../../coupon/types/coupon.types';

import {
  formatDate,
} from '../../../shared/utils/formatUtils';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

import Pagination
  from '../../../shared/components/Pagination';

import RouteConfig
  from '../../../routes/RouteConfig';


type ActiveTab =
  | 'point'
  | 'score'
  | 'coupon';


type CouponStatusFilter =
  | 'ALL'
  | 'AVAILABLE'
  | 'USED'
  | 'EXPIRED'
  | 'CANCELLED';


const ITEMS_PER_PAGE =
  10;

const MEMO_PREVIEW_LENGTH =
  10;

const POINT_STEP =
  5000;

const POINT_BUTTON_STEP =
  10000;


const QUICK_POINT_AMOUNTS = [
  5000,
  10000,
  30000,
  50000,
  100000,
  500000,
] as const;


const parsePointInput = (
  value: string,
): number => {

  const normalized =
    value.replace(
      /[^0-9]/g,
      '',
    );


  return normalized
    ? Number(
        normalized,
      )
    : 0;
};


const formatPointInput = (
  value: string,
): string => {

  const amount =
    parsePointInput(
      value,
    );


  return amount > 0
    ? amount.toLocaleString(
        'ko-KR',
      )
    : '';
};


const getMemoPreview = (
  memo: string,
): string => {

  const normalizedMemo =
    memo.trim();


  if (
    normalizedMemo.length <=
    MEMO_PREVIEW_LENGTH
  ) {
    return normalizedMemo;
  }


  return `${normalizedMemo.slice(
    0,
    MEMO_PREVIEW_LENGTH,
  )}…`;
};


const formatNumber = (
  value:
    number | null | undefined,
): string => {

  if (
    value == null
  ) {
    return '-';
  }


  return value.toLocaleString(
    'ko-KR',
  );
};


const formatDateTime = (
  value:
    string | null | undefined,
): string => {

  if (!value) {
    return '-';
  }


  const date =
    new Date(
      value,
    );


  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return value;
  }


  return date.toLocaleString(
    'ko-KR',
  );
};


const formatSimpleDate = (
  value:
    string | null | undefined,
): string => {

  if (!value) {
    return '-';
  }


  const date =
    new Date(
      value,
    );


  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return value;
  }


  return new Intl.DateTimeFormat(
    'ko-KR',
    {
      year:
        'numeric',

      month:
        '2-digit',

      day:
        '2-digit',
    },
  ).format(
    date,
  );
};


const getRewardLabel = (
  rewardType:
    CouponRewardType | null | undefined,

  rewardAmount:
    number | null | undefined,
): string => {

  if (!rewardType) {
    return '-';
  }


  switch (
    rewardType
  ) {

    case 'POINT':
      return `${formatNumber(
        rewardAmount,
      )}P`;


    case 'STORE_POINT':
      return `${formatNumber(
        rewardAmount,
      )} 매장P`;


    case 'FREE_GAME':
      return `무료게임 ${formatNumber(
        rewardAmount,
      )}회`;


    case 'SATELLITE':
      return `새틀라이트 ${formatNumber(
        rewardAmount,
      )}회`;


    case 'SEAT':
      return `시트권 ${formatNumber(
        rewardAmount,
      )}매`;


    case 'ETC':
      return `기타 보상 ${formatNumber(
        rewardAmount,
      )}`;


    default:
      return '-';
  }
};


const getCouponTemplateRewardLabel = (
  coupon: Coupon,
): string => {

  if (
    coupon.rewardMode ===
    'RANDOM'
  ) {

    return `랜덤 보상 ${
      coupon.rewardOptions
        ?.length ??
      0
    }종`;
  }


  return getRewardLabel(
    coupon.rewardType,
    coupon.rewardAmount,
  );
};


const getUserCouponRewardLabel = (
  coupon:
    AdminUserCoupon,
): string => {

  if (
    coupon.rewardMode ===
      'RANDOM' &&
    coupon.status ===
      'USED'
  ) {

    return getRewardLabel(
      coupon
        .selectedRewardType,

      coupon
        .selectedRewardAmount,
    );
  }


  if (
    coupon.rewardMode ===
    'RANDOM'
  ) {
    return '랜덤 보상';
  }


  return getRewardLabel(
    coupon.rewardType,
    coupon.rewardAmount,
  );
};


const getCouponStatusLabel = (
  status:
    AdminUserCoupon['status'],
): string => {

  switch (
    status
  ) {

    case 'AVAILABLE':
      return '사용 가능';


    case 'USED':
      return '사용 완료';


    case 'EXPIRED':
      return '기간 만료';


    case 'CANCELLED':
      return '취소';


    default:
      return status;
  }
};


const getCouponStatusClassName = (
  status:
    AdminUserCoupon['status'],
): string => {

  switch (
    status
  ) {

    case 'AVAILABLE':
      return `
        bg-emerald-50
        text-emerald-700
      `;


    case 'USED':
      return `
        bg-gray-100
        text-gray-600
      `;


    case 'EXPIRED':
      return `
        bg-orange-50
        text-orange-700
      `;


    case 'CANCELLED':
      return `
        bg-red-50
        text-red-600
      `;


    default:
      return `
        bg-gray-100
        text-gray-600
      `;
  }
};


const AdminMemberDetailPage:
  React.FC = () => {

  const {
    userId,
  } =
    useParams<{
      userId: string;
    }>();


  const navigate =
    useNavigate();


  const location =
    useLocation();


  const modal =
    useModal();


  /**
   * =========================================================
   * 로그인 관리자 정보
   *
   * ADMIN
   * -> 본인 매장 쿠폰만 발급 가능
   *
   * SUPERADMIN
   * -> 모든 매장 쿠폰 발급 가능
   * =========================================================
   */
  const authUser =
    useSelector(
      (
        state: RootState,
      ) =>
        state.auth.user,
    );


  const isSuperAdmin =
    authUser?.role ===
    'SUPERADMIN';


  const adminStoreId =
    authUser?.storeId ??
    null;


  /*
   * =========================
   * 회원
   * =========================
   */
  const [
    user,
    setUser,
  ] =
    useState<MemberInfo | null>(
      null,
    );


  const [
    userMap,
    setUserMap,
  ] =
    useState<
      Map<number, MemberInfo>
    >(
      new Map(),
    );


  /*
   * =========================
   * 매장
   * =========================
   */
  const [
    stores,
    setStores,
  ] =
    useState<StoreListItem[]>(
      [],
    );


  const [
    selectedStoreId,
    setSelectedStoreId,
  ] =
    useState(
      0,
    );


  /*
   * =========================
   * Page
   * =========================
   */
  const [
    loading,
    setLoading,
  ] =
    useState(
      true,
    );


  const [
    currentPage,
    setCurrentPage,
  ] =
    useState(
      1,
    );


  const [
    activeTab,
    setActiveTab,
  ] =
    useState<ActiveTab>(
      'point',
    );


  /*
   * =========================
   * Point
   * =========================
   */
  const [
    pointInput,
    setPointInput,
  ] =
    useState(
      '',
    );


  const [
    pointInputError,
    setPointInputError,
  ] =
    useState(
      '',
    );


  const pointInputRef =
    useRef<HTMLInputElement>(
      null,
    );


  const [
    pointStoreId,
    setPointStoreId,
  ] =
    useState(
      0,
    );


  const [
    pointHistory,
    setPointHistory,
  ] =
    useState<HistoryItem[]>(
      [],
    );


  const [
    pointTotalCount,
    setPointTotalCount,
  ] =
    useState(
      0,
    );


  const [
    pointList,
    setPointList,
  ] =
    useState<StoreUserPoint[]>(
      [],
    );


  /*
   * =========================
   * Score
   * =========================
   */
  const [
    scoreInput,
    setScoreInput,
  ] =
    useState(
      '',
    );


  const [
    scoreHistory,
    setScoreHistory,
  ] =
    useState<HistoryItem[]>(
      [],
    );


  const [
    scoreTotalCount,
    setScoreTotalCount,
  ] =
    useState(
      0,
    );


  const [
    scoreList,
    setScoreList,
  ] =
    useState<StoreUserScore[]>(
      [],
    );


  /*
   * =========================
   * Coupon
   * =========================
   */
  const [
    userCoupons,
    setUserCoupons,
  ] =
    useState<
      AdminUserCoupon[]
    >(
      [],
    );


  const [
    couponTemplates,
    setCouponTemplates,
  ] =
    useState<Coupon[]>(
      [],
    );


  const [
    couponLoading,
    setCouponLoading,
  ] =
    useState(
      false,
    );


  const [
    couponProcessingId,
    setCouponProcessingId,
  ] =
    useState<
      number | null
    >(
      null,
    );


  const [
    couponStatusFilter,
    setCouponStatusFilter,
  ] =
    useState<CouponStatusFilter>(
      'AVAILABLE',
    );


  const [
    issueCouponModalOpen,
    setIssueCouponModalOpen,
  ] =
    useState(
      false,
    );


  const [
    selectedIssueCouponId,
    setSelectedIssueCouponId,
  ] =
    useState<
      number | null
    >(
      null,
    );


  const [
    issueMemo,
    setIssueMemo,
  ] =
    useState(
      '',
    );


  const [
    issuingCoupon,
    setIssuingCoupon,
  ] =
    useState(
      false,
    );


  /**
   * =========================================================
   * 현재 관리자가 발급할 수 있는 활성 쿠폰
   *
   * SUPERADMIN
   * -> 모든 매장의 활성 쿠폰
   *
   * ADMIN
   * -> 로그인 관리자의 storeId와 동일한 매장 쿠폰만
   *
   * 중요:
   * 이 필터는 화면 표시/UX용 방어입니다.
   * 실제 권한 검증은 백엔드에서도 반드시 유지해야 합니다.
   * =========================================================
   */
  const availableCouponTemplates =
    useMemo(
      () => {

        if (
          isSuperAdmin
        ) {
          return couponTemplates;
        }


        if (
          adminStoreId == null
        ) {
          return [];
        }


        return couponTemplates.filter(
          (coupon) =>
            coupon.storeId ===
            adminStoreId,
        );

      },
      [
        couponTemplates,
        isSuperAdmin,
        adminStoreId,
      ],
    );


  /*
   * =========================
   * Memo
   * =========================
   */
  const [
    memoEditingItem,
    setMemoEditingItem,
  ] =
    useState<HistoryItem | null>(
      null,
    );


  const [
    memoInput,
    setMemoInput,
  ] =
    useState(
      '',
    );


  const [
    isMemoSaving,
    setIsMemoSaving,
  ] =
    useState(
      false,
    );


  /*
   * =========================
   * 날짜
   * =========================
   */
  const now =
    useMemo(
      () =>
        new Date(),
      [],
    );


  const oneMonthAgo =
    useMemo(
      () =>
        new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
        ),
      [
        now,
      ],
    );


  const [
    startYear,
    setStartYear,
  ] =
    useState(
      oneMonthAgo.getFullYear(),
    );


  const [
    startMonth,
    setStartMonth,
  ] =
    useState(
      oneMonthAgo.getMonth() +
        1,
    );


  const [
    endYear,
    setEndYear,
  ] =
    useState(
      now.getFullYear(),
    );


  const [
    endMonth,
    setEndMonth,
  ] =
    useState(
      now.getMonth() +
        1,
    );


  /**
   * 조회 날짜 범위 생성
   */
  const createDateRange =
    useCallback(
      () => {

        const startDate =
          `${startYear}-${String(
            startMonth,
          ).padStart(
            2,
            '0',
          )}-01`;


        const lastDay =
          new Date(
            endYear,
            endMonth,
            0,
          ).getDate();


        const endDate =
          `${endYear}-${String(
            endMonth,
          ).padStart(
            2,
            '0',
          )}-${String(
            lastDay,
          ).padStart(
            2,
            '0',
          )}`;


        return {
          startDate,
          endDate,
        };
      },
      [
        startYear,
        startMonth,
        endYear,
        endMonth,
      ],
    );


  /**
   * 회원 / 매장 / 잔액 초기 조회
   */
  useEffect(
    () => {

      let active =
        true;


      const loadUserData =
        async (): Promise<void> => {

          if (!userId) {

            setLoading(
              false,
            );

            return;
          }


          try {

            setLoading(
              true,
            );


            const [
              allMembers,
              storeList,
            ] =
              await Promise.all([
                memberService
                  .getMembers(),

                storeService
                  .getStores(),
              ]);


            if (!active) {
              return;
            }


            const foundUser =
              allMembers.find(
                (member) =>
                  member.userId ===
                  userId,
              ) ??
              null;


            setStores(
              storeList,
            );


            setUser(
              foundUser,
            );


            const memberMap =
              new Map<
                number,
                MemberInfo
              >();


            allMembers.forEach(
              (member) => {

                memberMap.set(
                  member.id,
                  member,
                );

              },
            );


            setUserMap(
              memberMap,
            );


            if (!foundUser) {
              return;
            }


            setSelectedStoreId(
              foundUser.storeId ||
                0,
            );


            const [
              scoreListData,
              pointListData,
            ] =
              await Promise.all([
                rankingService
                  .getUserScoreList(
                    foundUser.userId,
                  ),

                seedService
                  .getUserPointList(
                    foundUser.userId,
                  ),
              ]);


            if (!active) {
              return;
            }


            setScoreList(
              scoreListData,
            );


            setPointList(
              pointListData,
            );

          } catch (
            error: unknown
          ) {

            console.error(
              '회원 상세 데이터 로딩 실패:',
              error,
            );


            if (active) {

              setUser(
                null,
              );

            }

          } finally {

            if (active) {

              setLoading(
                false,
              );

            }

          }
        };


      void loadUserData();


      return () => {

        active =
          false;

      };

    },
    [
      userId,
    ],
  );


  /**
   * 뒤로가기
   */
  const handleBack =
    (): void => {

      const state =
        location.state as
          | {
              from?: string;
            }
          | undefined;


      if (
        state?.from
      ) {

        navigate(
          state.from,
        );

        return;
      }


      navigate(
        RouteConfig
          .Management
          .Members
          .fullPath,
      );
    };


  /**
   * 회원 최신 정보 재조회
   */
  const refreshMember =
    async (): Promise<
      MemberInfo | null
    > => {

      if (!userId) {
        return null;
      }


      const members =
        await memberService
          .getMembers();


      const found =
        members.find(
          (member) =>
            member.userId ===
            userId,
        ) ??
        null;


      setUser(
        found,
      );


      return found;
    };


  /**
   * 포인트 데이터 새로고침
   */
  const refreshPointData =
    async (): Promise<void> => {

      if (!userId) {
        return;
      }


      await refreshMember();


      const {
        startDate,
        endDate,
      } =
        createDateRange();


      const [
        history,
        points,
      ] =
        await Promise.all([
          seedService
            .getPointHistory({
              userId,

              storeId:
                pointStoreId,

              startDate,

              endDate,

              page:
                currentPage -
                1,

              size:
                ITEMS_PER_PAGE,
            }),

          seedService
            .getUserPointList(
              userId,
            ),
        ]);


      setPointHistory(
        history.histories,
      );


      setPointTotalCount(
        history.totalCount,
      );


      setPointList(
        points,
      );
    };


  const pointLabel =
    pointStoreId ===
      0
      ? '라포이트 시드'
      : '매장 포인트';


  const pointAmount =
    parsePointInput(
      pointInput,
    );


  const selectedPointBalance =
    pointStoreId ===
      0
      ? user?.point ??
        0
      : pointList.find(
          (item) =>
            item.storeId ===
            pointStoreId,
        )?.point ??
        0;


  /**
   * 포인트 입력
   */
  const handlePointInputChange = (
    value: string,
  ): void => {

    const normalized =
      value.replace(
        /[^0-9]/g,
        '',
      );


    setPointInput(
      normalized,
    );


    if (
      pointInputError
    ) {

      setPointInputError(
        '',
      );

    }
  };


  const changePointByStep = (
    direction: 1 | -1,
  ): void => {

    setPointInputError(
      '',
    );


    setPointInput(
      (previous) => {

        const currentAmount =
          parsePointInput(
            previous,
          );


        const nextAmount =
          Math.max(
            0,

            currentAmount +
              POINT_BUTTON_STEP *
                direction,
          );


        return nextAmount >
          0
          ? String(
              nextAmount,
            )
          : '';
      },
    );
  };


  const addQuickPointAmount = (
    amount: number,
  ): void => {

    setPointInputError(
      '',
    );


    setPointInput(
      (previous) =>
        String(
          parsePointInput(
            previous,
          ) +
            amount,
        ),
    );
  };


  const requestPointInputAgain =
    async (
      message: string,
    ): Promise<null> => {

      setPointInputError(
        message,
      );


      await modal.alert(
        message,
      );


      setPointInput(
        '',
      );


      window.requestAnimationFrame(
        () => {

          pointInputRef
            .current
            ?.focus();

        },
      );


      return null;
    };


  const validatePointAmount =
    async (): Promise<
      number | null
    > => {

      const point =
        parsePointInput(
          pointInput,
        );


      if (
        point <=
        0
      ) {

        return requestPointInputAgain(
          `${pointLabel}를 입력해주세요.`,
        );

      }


      if (
        point %
          POINT_STEP !==
        0
      ) {

        return requestPointInputAgain(
          '5,000 단위로 다시 입력해주세요.',
        );

      }


      setPointInputError(
        '',
      );


      return point;
    };


  /**
   * 시드 / 매장 포인트 지급
   */
  const handleAddPoint =
    async (): Promise<void> => {

      if (!user) {
        return;
      }


      const point =
        await validatePointAmount();


      if (
        point ===
        null
      ) {
        return;
      }


      try {

        if (
          pointStoreId ===
          0
        ) {

          await seedService
            .updateUserPoint(
              user.userId,
              point,
            );

        } else {

          await seedService
            .grantUserStorePoint(
              user.userId,
              pointStoreId,
              point,
            );

        }


        window.dispatchEvent(
          new Event(
            'balance-refresh',
          ),
        );


        await modal.alert(
          `+${point.toLocaleString(
            'ko-KR',
          )} ${pointLabel}가 추가되었습니다.`,
        );


        setPointInput(
          '',
        );


        setPointInputError(
          '',
        );


        await refreshPointData();

      } catch (
        error: unknown
      ) {

        console.error(
          `${pointLabel} 추가 실패:`,
          error,
        );


        await modal.alert(
          `${pointLabel} 추가에 실패했습니다. 다시 시도해주세요.`,
        );

      }
    };


  /**
   * 선택 포인트 최신 잔액
   */
  const fetchLatestSelectedBalance =
    async (): Promise<number> => {

      if (!user) {
        return 0;
      }


      if (
        pointStoreId ===
        0
      ) {

        const latestUser =
          await refreshMember();


        return (
          latestUser?.point ??
          0
        );
      }


      const latestPointList =
        await seedService
          .getUserPointList(
            user.userId,
          );


      setPointList(
        latestPointList,
      );


      return (
        latestPointList.find(
          (item) =>
            item.storeId ===
            pointStoreId,
        )?.point ??
        0
      );
    };


  /**
   * 시드 / 매장 포인트 차감
   */
  const handleDeductPoint =
    async (): Promise<void> => {

      if (!user) {
        return;
      }


      const point =
        await validatePointAmount();


      if (
        point ===
        null
      ) {
        return;
      }


      try {

        const latestBalance =
          await fetchLatestSelectedBalance();


        const afterBalance =
          latestBalance -
          point;


        if (
          afterBalance <
          0
        ) {

          const message =
            `${pointLabel} 잔액이 부족합니다. ` +
            `현재 잔액은 ${latestBalance.toLocaleString(
              'ko-KR',
            )}입니다.`;


          setPointInputError(
            message,
          );


          await modal.alert(
            message,
          );


          window.requestAnimationFrame(
            () => {

              pointInputRef
                .current
                ?.focus();

            },
          );


          return;
        }


        const confirmed =
          await modal.confirm(
            `${point.toLocaleString(
              'ko-KR',
            )} ${pointLabel}를 차감하시겠습니까?\n` +
              `차감 후 잔액: ${afterBalance.toLocaleString(
                'ko-KR',
              )}`,
          );


        if (!confirmed) {
          return;
        }


        if (
          pointStoreId ===
          0
        ) {

          await seedService
            .updateUserPoint(
              user.userId,
              -point,
            );

        } else {

          await seedService
            .grantUserStorePoint(
              user.userId,
              pointStoreId,
              -point,
            );

        }


        window.dispatchEvent(
          new Event(
            'balance-refresh',
          ),
        );


        await modal.alert(
          `-${point.toLocaleString(
            'ko-KR',
          )} ${pointLabel}가 차감되었습니다.`,
        );


        setPointInput(
          '',
        );


        setPointInputError(
          '',
        );


        await refreshPointData();

      } catch (
        error: unknown
      ) {

        console.error(
          `${pointLabel} 차감 실패:`,
          error,
        );


        try {

          await refreshPointData();

        } catch (
          refreshError
        ) {

          console.error(
            '잔액 재조회 실패:',
            refreshError,
          );

        }


        await modal.alert(
          `${pointLabel} 차감에 실패했습니다. 잔액을 확인한 후 다시 시도해주세요.`,
        );

      }
    };


  /**
   * 스코어 추가 / 차감
   */
  const handleAddScore =
    async (): Promise<void> => {

      if (
        !user ||
        !scoreInput.trim()
      ) {

        await modal.alert(
          '스코어를 입력해주세요.',
        );

        return;
      }


      if (
        !selectedStoreId
      ) {

        await modal.alert(
          '지점을 선택해주세요.',
        );

        return;
      }


      const score =
        Number.parseInt(
          scoreInput,
          10,
        );


      if (
        Number.isNaN(
          score,
        )
      ) {

        await modal.alert(
          '올바른 숫자를 입력해주세요.',
        );

        return;
      }


      try {

        await rankingService
          .updateUserScore(
            user.userId,
            score,
            selectedStoreId,
          );


        await modal.alert(
          `${
            score >
            0
              ? '+'
              : ''
          }${score.toLocaleString(
            'ko-KR',
          )} 스코어가 추가되었습니다.`,
        );


        setScoreInput(
          '',
        );


        const {
          startDate,
          endDate,
        } =
          createDateRange();


        const [
          history,
          scores,
        ] =
          await Promise.all([
            rankingService
              .getUserScoreHistory({
                userId:
                  user.userId,

                storeId:
                  selectedStoreId,

                startDate,

                endDate,

                page:
                  currentPage -
                    1,

                size:
                  ITEMS_PER_PAGE,
              }),

            rankingService
              .getUserScoreList(
                user.userId,
              ),
          ]);


        setScoreHistory(
          history.histories,
        );


        setScoreTotalCount(
          history.totalCount,
        );


        setScoreList(
          scores,
        );

      } catch (
        error: unknown
      ) {

        console.error(
          '스코어 추가 실패:',
          error,
        );


        await modal.alert(
          '스코어 추가에 실패했습니다. 다시 시도해주세요.',
        );

      }
    };


  /**
   * Score history
   */
  useEffect(
    () => {

      if (
        activeTab !==
          'score' ||
        !userId ||
        !selectedStoreId
      ) {
        return;
      }


      const loadHistory =
        async (): Promise<void> => {

          const {
            startDate,
            endDate,
          } =
            createDateRange();


          try {

            const result =
              await rankingService
                .getUserScoreHistory({
                  userId,

                  storeId:
                    selectedStoreId,

                  startDate,

                  endDate,

                  page:
                    currentPage -
                    1,

                  size:
                    ITEMS_PER_PAGE,
                });


            setScoreHistory(
              result.histories,
            );


            setScoreTotalCount(
              result.totalCount,
            );

          } catch (
            error: unknown
          ) {

            console.error(
              '스코어 히스토리 로딩 실패:',
              error,
            );


            setScoreHistory(
              [],
            );


            setScoreTotalCount(
              0,
            );

          }
        };


      void loadHistory();

    },
    [
      userId,
      selectedStoreId,
      activeTab,
      currentPage,
      createDateRange,
    ],
  );


  /**
   * Point history
   */
  useEffect(
    () => {

      if (
        activeTab !==
          'point' ||
        !userId
      ) {
        return;
      }


      const loadHistory =
        async (): Promise<void> => {

          const {
            startDate,
            endDate,
          } =
            createDateRange();


          try {

            const result =
              await seedService
                .getPointHistory({
                  userId,

                  storeId:
                    pointStoreId,

                  startDate,

                  endDate,

                  page:
                    currentPage -
                    1,

                  size:
                    ITEMS_PER_PAGE,
                });


            setPointHistory(
              result.histories,
            );


            setPointTotalCount(
              result.totalCount,
            );

          } catch (
            error: unknown
          ) {

            console.error(
              '포인트 히스토리 로딩 실패:',
              error,
            );


            setPointHistory(
              [],
            );


            setPointTotalCount(
              0,
            );

          }
        };


      void loadHistory();

    },
    [
      userId,
      pointStoreId,
      activeTab,
      currentPage,
      createDateRange,
    ],
  );


  /**
   * =========================================================
   * Coupon
   * =========================================================
   */

  /**
   * 회원 쿠폰함 조회
   */
  const loadUserCoupons =
    useCallback(
      async (): Promise<void> => {

        if (!userId) {
          return;
        }


        try {

          setCouponLoading(
            true,
          );


          const data =
            await adminCouponService
              .getUserCoupons(
                userId,
              );


          setUserCoupons(
            data,
          );

        } catch (
          error: unknown
        ) {

          console.error(
            '회원 쿠폰 조회 실패:',
            error,
          );


          setUserCoupons(
            [],
          );


          await modal.alert(
            '회원 쿠폰함을 불러오지 못했습니다.',
          );

        } finally {

          setCouponLoading(
            false,
          );

        }

      },
      [
        userId,
        modal,
      ],
    );


  /**
   * 발급 가능한 활성 쿠폰 조회
   */
  const loadCouponTemplates =
    useCallback(
      async (): Promise<void> => {

        try {

          const data =
            await adminCouponService
              .getActiveCoupons();


          setCouponTemplates(
            data,
          );

        } catch (
          error: unknown
        ) {

          console.error(
            '활성 쿠폰 조회 실패:',
            error,
          );


          setCouponTemplates(
            [],
          );

        }

      },
      [],
    );


  /**
   * 쿠폰 탭 진입 시 조회
   */
  useEffect(
    () => {

      if (
        activeTab !==
        'coupon'
      ) {
        return;
      }


      void Promise.all([
        loadUserCoupons(),
        loadCouponTemplates(),
      ]);

    },
    [
      activeTab,
      loadUserCoupons,
      loadCouponTemplates,
    ],
  );


  /**
   * 쿠폰 발급
   */
  const handleIssueCoupon =
    async (): Promise<void> => {

      if (
        !user ||
        selectedIssueCouponId ==
          null ||
        issuingCoupon
      ) {
        return;
      }


      const selectedCoupon =
        availableCouponTemplates.find(
          (coupon) =>
            coupon.couponId ===
            selectedIssueCouponId,
        );


      if (!selectedCoupon) {

        await modal.alert(
          '현재 관리자 권한으로 발급할 수 없는 쿠폰입니다.',
        );

        return;
      }


      const confirmed =
        await modal.confirm(
          `"${selectedCoupon.couponName}" 쿠폰을\n${user.userName} 회원에게 발급하시겠습니까?`,
        );


      if (!confirmed) {
        return;
      }


      try {

        setIssuingCoupon(
          true,
        );


        await adminCouponService
          .issueCoupon(
            user.userId,
            selectedIssueCouponId,
            {
              memo:
                issueMemo.trim() ||
                null,
            },
          );


        await modal.alert(
          '쿠폰이 발급되었습니다.',
        );


        setIssueCouponModalOpen(
          false,
        );


        setSelectedIssueCouponId(
          null,
        );


        setIssueMemo(
          '',
        );


        await loadUserCoupons();

      } catch (
        error: unknown
      ) {

        console.error(
          '쿠폰 발급 실패:',
          error,
        );


        await modal.alert(
          '쿠폰 발급에 실패했습니다.',
        );

      } finally {

        setIssuingCoupon(
          false,
        );

      }
    };


  /**
   * 쿠폰 사용
   */
  const handleUseCoupon =
    async (
      coupon:
        AdminUserCoupon,
    ): Promise<void> => {

      if (
        coupon.status !==
          'AVAILABLE' ||
        couponProcessingId !=
          null
      ) {
        return;
      }


      /**
       * RANDOM 쿠폰은 관리자가 사용할 수 없음
       * USER가 자신의 쿠폰함에서 직접 사용
       */
      if (
        coupon.rewardMode ===
        'RANDOM'
      ) {

        await modal.alert(
          '랜덤 쿠폰은 회원이 직접 사용해야 합니다.',
        );

        return;
      }


      const confirmed =
        await modal.confirm(
          `"${coupon.couponName}" 쿠폰을 사용 처리하시겠습니까?`,
        );


      if (!confirmed) {
        return;
      }


      try {

        setCouponProcessingId(
          coupon.userCouponId,
        );


        const updated =
          await adminCouponService
            .useUserCoupon(
              coupon.userCouponId,
            );


        setUserCoupons(
          (previous) =>
            previous.map(
              (item) =>
                item.userCouponId ===
                  updated.userCouponId
                  ? updated
                  : item,
            ),
        );


        /**
         * 쿠폰 보상이 포인트일 경우
         * 회원 정보 / 잔액도 갱신
         */
        await Promise.all([
          refreshMember(),

          seedService
            .getUserPointList(
              userId!,
            )
            .then(
              setPointList,
            ),
        ]);


        window.dispatchEvent(
          new Event(
            'balance-refresh',
          ),
        );


        if (
          updated.rewardMode ===
          'RANDOM'
        ) {

          const reward =
            getRewardLabel(
              updated
                .selectedRewardType,

              updated
                .selectedRewardAmount,
            );


          await modal.alert(
            `랜덤 쿠폰 사용이 완료되었습니다.\n당첨 보상: ${reward}`,
          );

        } else {

          await modal.alert(
            '쿠폰 사용 처리가 완료되었습니다.',
          );

        }

      } catch (
        error: unknown
      ) {

        console.error(
          '쿠폰 사용 실패:',
          error,
        );


        await modal.alert(
          '쿠폰 사용 처리에 실패했습니다.',
        );

      } finally {

        setCouponProcessingId(
          null,
        );

      }
    };


  /**
   * 쿠폰 취소
   */
  const handleCancelCoupon =
    async (
      coupon:
        AdminUserCoupon,
    ): Promise<void> => {

      if (
        coupon.status !==
          'AVAILABLE' ||
        couponProcessingId !=
          null
      ) {
        return;
      }


      const confirmed =
        await modal.confirm(
          `"${coupon.couponName}" 쿠폰을 취소하시겠습니까?`,
        );


      if (!confirmed) {
        return;
      }


      try {

        setCouponProcessingId(
          coupon.userCouponId,
        );


        const updated =
          await adminCouponService
            .cancelUserCoupon(
              coupon.userCouponId,
            );


        setUserCoupons(
          (previous) =>
            previous.map(
              (item) =>
                item.userCouponId ===
                  updated.userCouponId
                  ? updated
                  : item,
            ),
        );


        await modal.alert(
          '쿠폰이 취소되었습니다.',
        );

      } catch (
        error: unknown
      ) {

        console.error(
          '쿠폰 취소 실패:',
          error,
        );


        await modal.alert(
          '쿠폰 취소에 실패했습니다.',
        );

      } finally {

        setCouponProcessingId(
          null,
        );

      }
    };


  const filteredUserCoupons =
    useMemo(
      () => {

        if (
          couponStatusFilter ===
          'ALL'
        ) {
          return userCoupons;
        }


        return userCoupons.filter(
          (coupon) =>
            coupon.status ===
            couponStatusFilter,
        );

      },
      [
        userCoupons,
        couponStatusFilter,
      ],
    );


  const availableCouponCount =
    useMemo(
      () =>
        userCoupons.filter(
          (coupon) =>
            coupon.status ===
            'AVAILABLE',
        ).length,
      [
        userCoupons,
      ],
    );


  /**
   * ============================
   * 기존 내역 데이터
   * ============================
   */
  const currentData =
    activeTab ===
      'score'
      ? scoreHistory
      : pointHistory;


  const totalCount =
    activeTab ===
      'score'
      ? scoreTotalCount
      : pointTotalCount;


  const currentYear =
    now.getFullYear();


  const yearOptions =
    Array.from(
      {
        length:
          currentYear -
          2020 +
          1,
      },
      (
        _,
        index,
      ) =>
        2020 +
        index,
    );


  const monthOptions =
    Array.from(
      {
        length: 12,
      },
      (
        _,
        index,
      ) =>
        index +
        1,
    );


  /**
   * 지점별 Score + Point
   */
  const balanceRows =
    useMemo(
      () => {

        const merged =
          new Map<
            number,
            {
              score: number;
              point: number;
            }
          >();


        scoreList.forEach(
          (item) => {

            merged.set(
              item.storeId,
              {
                score:
                  item.score,

                point:
                  0,
              },
            );

          },
        );


        pointList.forEach(
          (item) => {

            const existing =
              merged.get(
                item.storeId,
              );


            merged.set(
              item.storeId,
              {
                score:
                  existing?.score ??
                  0,

                point:
                  item.point,
              },
            );

          },
        );


        return Array.from(
          merged.entries(),
        )
          .filter(
            ([
              storeId,
              value,
            ]) =>
              storeId !==
                0 &&
              (
                value.score !==
                  0 ||
                value.point !==
                  0
              ),
          )
          .map(
            ([
              storeId,
              value,
            ]) => ({
              storeId,

              storeName:
                stores.find(
                  (store) =>
                    store.storeId ===
                    storeId,
                )?.storeName ??
                `지점 ${storeId}`,

              ...value,
            }),
          );

      },
      [
        scoreList,
        pointList,
        stores,
      ],
    );


  const storeNameMap =
    useMemo(
      () => {

        const map =
          new Map<
            number,
            string
          >();


        stores.forEach(
          (store) => {

            map.set(
              store.storeId,
              store.storeName,
            );

          },
        );


        return map;

      },
      [
        stores,
      ],
    );


  /**
   * 내역 변동값
   */
  const getHistoryDelta = (
    item:
      HistoryItem,
  ): number => {

    if (
      activeTab ===
      'score'
    ) {
      return item.scoreDelta;
    }


    return item.pointDelta;
  };


  /**
   * 처리자
   */
  const getActorDisplay = (
    item:
      HistoryItem,
  ): string => {

    if (
      item.actorUserId ===
      null
    ) {
      return '시스템';
    }


    const actor =
      userMap.get(
        item.actorUserId,
      );


    return actor
      ? actor.userName
      : `#${item.actorUserId}`;
  };


  /**
   * 메모
   */
  const openMemoEditor = (
    item:
      HistoryItem,
  ): void => {

    setMemoEditingItem(
      item,
    );


    setMemoInput(
      item.memo?.trim() ??
        '',
    );
  };


  const closeMemoEditor =
    (): void => {

      if (
        isMemoSaving
      ) {
        return;
      }


      setMemoEditingItem(
        null,
      );


      setMemoInput(
        '',
      );
    };


  const applyMemoToHistory = (
    historyId:
      number,

    memo:
      string,
  ): void => {

    const updateItem = (
      item:
        HistoryItem,
    ): HistoryItem =>
      item.historyId ===
      historyId
        ? {
            ...item,
            memo,
          }
        : item;


    setPointHistory(
      (previous) =>
        previous.map(
          updateItem,
        ),
    );


    setScoreHistory(
      (previous) =>
        previous.map(
          updateItem,
        ),
    );
  };


  const handleSaveMemo =
    async (): Promise<void> => {

      if (
        !memoEditingItem
      ) {
        return;
      }


      const memoValue =
        memoInput.trim();


      if (
        !memoValue
      ) {

        await modal.alert(
          '메모 내용을 입력해주세요.',
        );

        return;
      }


      try {

        setIsMemoSaving(
          true,
        );


        await historyService
          .updateMemo(
            memoEditingItem
              .historyId,

            memoValue,
          );


        applyMemoToHistory(
          memoEditingItem
            .historyId,

          memoValue,
        );


        await modal.alert(
          '메모가 저장되었습니다.',
        );


        setMemoEditingItem(
          null,
        );


        setMemoInput(
          '',
        );

      } catch (
        error: unknown
      ) {

        console.error(
          '메모 저장 실패:',
          error,
        );


        await modal.alert(
          '메모 저장에 실패했습니다. 다시 시도해주세요.',
        );

      } finally {

        setIsMemoSaving(
          false,
        );

      }
    };


  if (
    loading
  ) {

    return (
      <div
        className="
          flex
          min-h-full
          items-center
          justify-center
          px-4
          py-20
        "
      >

        <div
          className="
            text-center
          "
        >

          <span
            className="
              mx-auto
              block
              h-9
              w-9
              animate-spin
              rounded-full
              border-4
              border-gray-200
              border-t-main
            "
          />


          <p
            className="
              mt-4
              text-sm
              font-medium
              text-gray-500
            "
          >
            회원 정보를 불러오는 중입니다.
          </p>

        </div>

      </div>
    );
  }


  if (
    !user
  ) {

    return (
      <div
        className="
          flex
          min-h-full
          flex-col
          items-center
          justify-center
          gap-4
          px-4
          py-20
          text-center
        "
      >

        <UserRound
          size={42}
          className="
            text-gray-300
          "
        />


        <div>

          <p
            className="
              text-base
              font-semibold
              text-gray-800
            "
          >
            회원을 찾을 수 없습니다.
          </p>

        </div>


        <button
          type="button"
          onClick={
            handleBack
          }
          className="
            rounded-lg
            bg-gray-900
            px-4
            py-2.5
            text-sm
            font-semibold
            text-white
          "
        >
          회원 목록으로
        </button>

      </div>
    );
  }


  return (
    <div
      className="
        min-h-full
        w-full
        bg-gray-50
        px-4
        pb-8
        pt-4
        sm:px-6
        sm:pt-6
        lg:px-8
      "
    >

      <div
        className="
          mx-auto
          w-full
          max-w-7xl
        "
      >

        {/* =====================================================
            Back
            ===================================================== */}
        <button
          type="button"
          onClick={
            handleBack
          }
          className="
            mb-4
            inline-flex
            h-9
            items-center
            gap-1.5
            rounded-lg
            px-2
            text-sm
            font-semibold
            text-gray-600
            transition
            hover:bg-gray-200/70
            hover:text-gray-900
          "
        >
          <ArrowLeft
            size={18}
          />

          회원 목록
        </button>


        {/* =====================================================
            회원 정보
            ===================================================== */}
        <section
          className="
            mb-4
            overflow-hidden
            rounded-2xl
            border
            border-gray-200
            bg-white
            shadow-sm
          "
        >

          <div
            className="
              flex
              flex-col
              gap-4
              p-5
              sm:p-6
              lg:flex-row
              lg:items-center
              lg:justify-between
            "
          >

            <div
              className="
                flex
                min-w-0
                items-start
                gap-4
              "
            >

              <div
                className="
                  flex
                  h-12
                  w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-xl
                  bg-gray-900
                  text-white
                "
              >
                <UserRound
                  size={23}
                />
              </div>


              <div
                className="
                  min-w-0
                "
              >

                <div
                  className="
                    flex
                    flex-wrap
                    items-center
                    gap-x-3
                    gap-y-1
                  "
                >

                  <h1
                    className="
                      text-xl
                      font-bold
                      text-gray-900
                      sm:text-2xl
                    "
                  >
                    {user.userName}
                  </h1>


                  <span
                    className="
                      text-sm
                      font-medium
                      text-gray-400
                    "
                  >
                    #{user.userId}
                  </span>

                </div>


                <div
                  className="
                    mt-2
                    flex
                    flex-wrap
                    gap-x-5
                    gap-y-1
                    text-sm
                    text-gray-500
                  "
                >

                  <span>
                    {user.phoneNumber ??
                      '전화번호 없음'}
                  </span>


                  <span>
                    가입일{' '}
                    {formatDate(
                      user.createAt,
                    )}
                  </span>

                </div>

              </div>

            </div>


            <div
              className="
                grid
                grid-cols-2
                gap-2
                sm:flex
              "
            >

              <div
                className="
                  rounded-xl
                  bg-gray-50
                  px-4
                  py-3
                  sm:min-w-[160px]
                "
              >

                <p
                  className="
                    text-xs
                    font-semibold
                    text-gray-400
                  "
                >
                  라포이트 시드
                </p>


                <p
                  className="
                    mt-1
                    text-xl
                    font-bold
                    text-gray-900
                  "
                >
                  {user.point.toLocaleString(
                    'ko-KR',
                  )}

                  <span
                    className="
                      ml-1
                      text-xs
                      text-gray-400
                    "
                  >
                    P
                  </span>
                </p>

              </div>


              <div
                className="
                  rounded-xl
                  bg-gray-50
                  px-4
                  py-3
                  sm:min-w-[160px]
                "
              >

                <p
                  className="
                    text-xs
                    font-semibold
                    text-gray-400
                  "
                >
                  사용 가능 쿠폰
                </p>


                <p
                  className="
                    mt-1
                    text-xl
                    font-bold
                    text-gray-900
                  "
                >
                  {availableCouponCount}

                  <span
                    className="
                      ml-1
                      text-xs
                      text-gray-400
                    "
                  >
                    장
                  </span>
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            보유 자산
            ===================================================== */}
        <section
          className="
            mb-4
            overflow-hidden
            rounded-2xl
            border
            border-gray-200
            bg-white
            shadow-sm
          "
        >

          <div
            className="
              flex
              items-center
              gap-2
              border-b
              border-gray-100
              px-5
              py-4
            "
          >
            <WalletCards
              size={17}
              className="
                text-gray-500
              "
            />

            <h2
              className="
                text-sm
                font-bold
                text-gray-900
              "
            >
              보유 자산
            </h2>
          </div>


          <div
            className="
              overflow-x-auto
            "
          >

            <div
              className="
                min-w-[520px]
              "
            >

              <div
                className="
                  grid
                  grid-cols-[minmax(0,1fr)_130px_130px]
                  bg-gray-50
                  px-5
                  py-2.5
                  text-xs
                  font-semibold
                  text-gray-500
                "
              >

                <span>
                  지점
                </span>

                <span
                  className="
                    text-right
                  "
                >
                  Score
                </span>

                <span
                  className="
                    text-right
                  "
                >
                  Point
                </span>

              </div>


              <div
                className="
                  grid
                  grid-cols-[minmax(0,1fr)_130px_130px]
                  items-center
                  border-t
                  border-gray-100
                  px-5
                  py-3
                  text-sm
                "
              >

                <span
                  className="
                    font-medium
                    text-gray-700
                  "
                >
                  라포이트 시드
                </span>

                <span
                  className="
                    text-right
                    text-gray-300
                  "
                >
                  -
                </span>

                <span
                  className="
                    text-right
                    font-bold
                    text-gray-900
                  "
                >
                  {user.point.toLocaleString(
                    'ko-KR',
                  )}
                </span>

              </div>


              {balanceRows.map(
                (row) => (

                  <div
                    key={
                      row.storeId
                    }
                    className="
                      grid
                      grid-cols-[minmax(0,1fr)_130px_130px]
                      items-center
                      border-t
                      border-gray-100
                      px-5
                      py-3
                      text-sm
                    "
                  >

                    <span
                      className="
                        truncate
                        font-medium
                        text-gray-700
                      "
                    >
                      {row.storeName}
                    </span>

                    <span
                      className="
                        text-right
                        font-bold
                        text-gray-700
                      "
                    >
                      {row.score.toLocaleString(
                        'ko-KR',
                      )}
                    </span>

                    <span
                      className="
                        text-right
                        font-bold
                        text-gray-900
                      "
                    >
                      {row.point.toLocaleString(
                        'ko-KR',
                      )}
                    </span>

                  </div>

                ),
              )}

            </div>

          </div>

        </section>


        {/* =====================================================
            관리 탭
            ===================================================== */}
        <section
          className="
            mb-4
            overflow-hidden
            rounded-2xl
            border
            border-gray-200
            bg-white
            shadow-sm
          "
        >

          <div
            className="
              border-b
              border-gray-100
              p-4
            "
          >

            <div
              className="
                grid
                w-full
                grid-cols-3
                rounded-xl
                bg-gray-100
                p-1
                lg:w-[510px]
              "
            >

              <button
                type="button"
                onClick={() => {

                  setActiveTab(
                    'point',
                  );

                  setCurrentPage(
                    1,
                  );

                }}
                className={`
                  flex
                  h-10
                  items-center
                  justify-center
                  gap-1.5
                  rounded-lg
                  text-xs
                  font-bold
                  transition
                  sm:text-sm

                  ${
                    activeTab ===
                    'point'
                      ? `
                        bg-white
                        text-gray-900
                        shadow-sm
                      `
                      : `
                        text-gray-500
                      `
                  }
                `}
              >
                <Coins
                  size={16}
                />

                포인트
              </button>


              <button
                type="button"
                onClick={() => {

                  setActiveTab(
                    'score',
                  );

                  setCurrentPage(
                    1,
                  );

                }}
                className={`
                  flex
                  h-10
                  items-center
                  justify-center
                  gap-1.5
                  rounded-lg
                  text-xs
                  font-bold
                  transition
                  sm:text-sm

                  ${
                    activeTab ===
                    'score'
                      ? `
                        bg-white
                        text-gray-900
                        shadow-sm
                      `
                      : `
                        text-gray-500
                      `
                  }
                `}
              >
                <Trophy
                  size={16}
                />

                스코어
              </button>


              <button
                type="button"
                onClick={() => {

                  setActiveTab(
                    'coupon',
                  );

                  setCurrentPage(
                    1,
                  );

                }}
                className={`
                  flex
                  h-10
                  items-center
                  justify-center
                  gap-1.5
                  rounded-lg
                  text-xs
                  font-bold
                  transition
                  sm:text-sm

                  ${
                    activeTab ===
                    'coupon'
                      ? `
                        bg-white
                        text-gray-900
                        shadow-sm
                      `
                      : `
                        text-gray-500
                      `
                  }
                `}
              >
                <Ticket
                  size={16}
                />

                쿠폰
              </button>

            </div>

          </div>


          {/* ===================================================
              POINT
              =================================================== */}
          {activeTab ===
            'point' && (

            <div
              className="
                grid
                gap-5
                p-5
                lg:grid-cols-2
                lg:p-6
              "
            >

              <div>

                <label
                  className="
                    text-xs
                    font-bold
                    text-gray-500
                  "
                >
                  관리할 포인트
                </label>


                <select
                  value={
                    pointStoreId
                  }
                  onChange={(
                    event,
                  ) => {

                    setPointStoreId(
                      Number(
                        event.target.value,
                      ),
                    );


                    setCurrentPage(
                      1,
                    );

                  }}
                  className="
                    mt-2
                    h-11
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    bg-white
                    px-3
                    text-sm
                    font-medium
                    outline-none
                    focus:border-main
                    focus:ring-2
                    focus:ring-main/10
                  "
                >

                  <option
                    value={0}
                  >
                    라포이트 시드
                  </option>


                  {stores.map(
                    (store) => (

                      <option
                        key={
                          store.storeId
                        }
                        value={
                          store.storeId
                        }
                      >
                        {store.storeName}{' '}
                        매장 포인트
                      </option>

                    ),
                  )}

                </select>


                <div
                  className="
                    mt-4
                    rounded-xl
                    bg-gray-50
                    p-4
                  "
                >

                  <p
                    className="
                      text-xs
                      font-semibold
                      text-gray-400
                    "
                  >
                    현재 잔액
                  </p>


                  <p
                    className="
                      mt-1
                      text-3xl
                      font-bold
                      text-gray-900
                    "
                  >
                    {selectedPointBalance.toLocaleString(
                      'ko-KR',
                    )}

                    <span
                      className="
                        ml-1
                        text-base
                        font-semibold
                        text-gray-400
                      "
                    >
                      P
                    </span>
                  </p>


                  <p
                    className="
                      mt-1
                      text-xs
                      text-gray-400
                    "
                  >
                    {pointLabel}
                  </p>

                </div>

              </div>


              <div>

                <label
                  className="
                    text-xs
                    font-bold
                    text-gray-500
                  "
                >
                  변경 금액
                </label>


                <div
                  className="
                    mt-2
                    grid
                    grid-cols-[76px_minmax(0,1fr)_76px]
                    gap-2
                  "
                >

                  <button
                    type="button"
                    onClick={() =>
                      changePointByStep(
                        -1,
                      )
                    }
                    disabled={
                      pointAmount <=
                      0
                    }
                    className="
                      h-11
                      rounded-xl
                      border
                      border-gray-300
                      bg-white
                      text-sm
                      font-bold
                      text-gray-600
                      disabled:opacity-40
                    "
                  >
                    −1만
                  </button>


                  <input
                    ref={
                      pointInputRef
                    }
                    value={
                      formatPointInput(
                        pointInput,
                      )
                    }
                    onChange={(
                      event,
                    ) =>
                      handlePointInputChange(
                        event.target.value,
                      )
                    }
                    inputMode="numeric"
                    placeholder="금액 입력"
                    className="
                      h-11
                      min-w-0
                      rounded-xl
                      border
                      border-gray-300
                      px-3
                      text-center
                      text-base
                      font-bold
                      outline-none
                      focus:border-main
                      focus:ring-2
                      focus:ring-main/10
                    "
                  />


                  <button
                    type="button"
                    onClick={() =>
                      changePointByStep(
                        1,
                      )
                    }
                    className="
                      h-11
                      rounded-xl
                      border
                      border-gray-300
                      bg-white
                      text-sm
                      font-bold
                      text-gray-600
                    "
                  >
                    +1만
                  </button>

                </div>


                {pointInputError && (
                  <p
                    className="
                      mt-2
                      text-xs
                      font-medium
                      text-red-600
                    "
                  >
                    {pointInputError}
                  </p>
                )}


                <div
                  className="
                    mt-4
                  "
                >

                  <p
                    className="
                      mb-2
                      text-xs
                      font-semibold
                      text-gray-400
                    "
                  >
                    빠른 금액 추가
                  </p>


                  <div
                    className="
                      grid
                      grid-cols-6
                      gap-1.5
                    "
                  >

                    {QUICK_POINT_AMOUNTS.map(
                      (amount) => (

                        <button
                          key={
                            amount
                          }
                          type="button"
                          onClick={() =>
                            addQuickPointAmount(
                              amount,
                            )
                          }
                          className="
                            rounded-lg
                            border
                            border-gray-200
                            bg-gray-50
                            px-3
                            py-1.5
                            text-xs
                            font-bold
                            text-gray-600
                            hover:border-main
                            hover:text-main
                          "
                        >
                          +
                          {amount.toLocaleString(
                            'ko-KR',
                          )}
                        </button>

                      ),
                    )}

                  </div>

                </div>


                <div
                  className="
                    mt-5
                    grid
                    grid-cols-2
                    gap-2
                  "
                >

                  <button
                    type="button"
                    onClick={() =>
                      void handleAddPoint()
                    }
                    disabled={
                      pointAmount <=
                      0
                    }
                    className="
                      h-11
                      rounded-xl
                      bg-main
                      px-3
                      text-sm
                      font-bold
                      text-white
                      disabled:opacity-40
                    "
                  >
                    {pointAmount >
                    0
                      ? `${pointAmount.toLocaleString(
                          'ko-KR',
                        )}P 지급`
                      : '지급'}
                  </button>


                  <button
                    type="button"
                    onClick={() =>
                      void handleDeductPoint()
                    }
                    disabled={
                      pointAmount <=
                        0 ||
                      pointAmount >
                        selectedPointBalance
                    }
                    className="
                      h-11
                      rounded-xl
                      border
                      border-red-200
                      bg-white
                      px-3
                      text-sm
                      font-bold
                      text-red-600
                      disabled:opacity-40
                    "
                  >
                    {pointAmount >
                    0
                      ? `${pointAmount.toLocaleString(
                          'ko-KR',
                        )}P 차감`
                      : '차감'}
                  </button>

                </div>

              </div>

            </div>

          )}


          {/* ===================================================
              SCORE
              =================================================== */}
          {activeTab ===
            'score' && (

            <div
              className="
                grid
                gap-5
                p-5
                lg:grid-cols-2
                lg:p-6
              "
            >

              <div>

                <label
                  className="
                    text-xs
                    font-bold
                    text-gray-500
                  "
                >
                  지점
                </label>


                <select
                  value={
                    selectedStoreId
                  }
                  onChange={(
                    event,
                  ) => {

                    setSelectedStoreId(
                      Number(
                        event.target.value,
                      ),
                    );


                    setCurrentPage(
                      1,
                    );

                  }}
                  className="
                    mt-2
                    h-11
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    bg-white
                    px-3
                    text-sm
                    font-medium
                    outline-none
                    focus:border-main
                  "
                >

                  <option
                    value={0}
                    disabled
                  >
                    지점을 선택해주세요
                  </option>


                  {stores.map(
                    (store) => (

                      <option
                        key={
                          store.storeId
                        }
                        value={
                          store.storeId
                        }
                      >
                        {store.storeName}
                      </option>

                    ),
                  )}

                </select>


                <div
                  className="
                    mt-4
                    rounded-xl
                    bg-gray-50
                    p-4
                  "
                >

                  <p
                    className="
                      text-xs
                      font-semibold
                      text-gray-400
                    "
                  >
                    선택 지점 스코어
                  </p>


                  <p
                    className="
                      mt-1
                      text-3xl
                      font-bold
                      text-gray-900
                    "
                  >
                    {(
                      scoreList.find(
                        (item) =>
                          item.storeId ===
                          selectedStoreId,
                      )?.score ??
                      0
                    ).toLocaleString(
                      'ko-KR',
                    )}
                  </p>

                </div>

              </div>


              <div>

                <label
                  className="
                    text-xs
                    font-bold
                    text-gray-500
                  "
                >
                  변경 스코어
                </label>


                <input
                  type="number"
                  value={
                    scoreInput
                  }
                  onChange={(
                    event,
                  ) =>
                    setScoreInput(
                      event.target.value,
                    )
                  }
                  placeholder="예: 10 또는 -10"
                  className="
                    mt-2
                    h-11
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    px-4
                    text-base
                    font-semibold
                    outline-none
                    focus:border-main
                  "
                />


                <p
                  className="
                    mt-2
                    text-xs
                    text-gray-400
                  "
                >
                  차감하려면 음수 값을 입력하세요.
                </p>


                <button
                  type="button"
                  onClick={() =>
                    void handleAddScore()
                  }
                  className="
                    mt-5
                    h-11
                    w-full
                    rounded-xl
                    bg-main
                    px-4
                    text-sm
                    font-bold
                    text-white
                  "
                >
                  스코어 적용
                </button>

              </div>

            </div>

          )}


          {/* ===================================================
              COUPON
              =================================================== */}
          {activeTab ===
            'coupon' && (

            <div
              className="
                p-4
                sm:p-5
                lg:p-6
              "
            >

              <div
                className="
                  flex
                  flex-col
                  gap-3
                  sm:flex-row
                  sm:items-center
                  sm:justify-between
                "
              >

                <div>

                  <h2
                    className="
                      flex
                      items-center
                      gap-2
                      text-base
                      font-bold
                      text-gray-900
                    "
                  >
                    <Ticket
                      size={18}
                    />

                    보유 쿠폰
                  </h2>


                  <p
                    className="
                      mt-1
                      text-xs
                      text-gray-500
                    "
                  >
                    전체 {userCoupons.length}장 · 사용 가능{' '}

                    <strong
                      className="
                        text-main
                      "
                    >
                      {availableCouponCount}장
                    </strong>
                  </p>

                </div>


                <button
                  type="button"
                  onClick={() => {

                    setSelectedIssueCouponId(
                      null,
                    );

                    setIssueMemo(
                      '',
                    );

                    setIssueCouponModalOpen(
                      true,
                    );

                  }}
                  className="
                    inline-flex
                    h-10
                    items-center
                    justify-center
                    gap-1.5
                    rounded-xl
                    bg-main
                    px-4
                    text-sm
                    font-bold
                    text-white
                  "
                >
                  <Plus
                    size={16}
                  />

                  쿠폰 발급
                </button>

              </div>


              <div
                className="
                  mt-4
                  flex
                  gap-2
                  overflow-x-auto
                  pb-1
                "
              >

                {(
                  [
                    [
                      'ALL',
                      '전체',
                    ],
                    [
                      'AVAILABLE',
                      '사용 가능',
                    ],
                    [
                      'USED',
                      '사용 완료',
                    ],
                    [
                      'EXPIRED',
                      '만료',
                    ],
                    [
                      'CANCELLED',
                      '취소',
                    ],
                  ] as const
                ).map(
                  ([
                    value,
                    label,
                  ]) => (

                    <button
                      key={
                        value
                      }
                      type="button"
                      onClick={() =>
                        setCouponStatusFilter(
                          value,
                        )
                      }
                      className={`
                        shrink-0
                        rounded-full
                        border
                        px-3
                        py-1.5
                        text-xs
                        font-semibold

                        ${
                          couponStatusFilter ===
                          value
                            ? `
                              border-main
                              bg-main
                              text-white
                            `
                            : `
                              border-gray-200
                              bg-white
                              text-gray-600
                            `
                        }
                      `}
                    >
                      {label}
                    </button>

                  ),
                )}

              </div>


              {couponLoading ? (

                <div
                  className="
                    flex
                    min-h-52
                    items-center
                    justify-center
                  "
                >

                  <span
                    className="
                      h-8
                      w-8
                      animate-spin
                      rounded-full
                      border-4
                      border-gray-200
                      border-t-main
                    "
                  />

                </div>

              ) : filteredUserCoupons.length ===
                0 ? (

                <div
                  className="
                    mt-5
                    flex
                    min-h-52
                    flex-col
                    items-center
                    justify-center
                    rounded-xl
                    border
                    border-dashed
                    border-gray-200
                    bg-gray-50
                    px-4
                    text-center
                  "
                >

                  <Ticket
                    size={30}
                    className="
                      text-gray-300
                    "
                  />


                  <p
                    className="
                      mt-3
                      text-sm
                      font-semibold
                      text-gray-600
                    "
                  >
                    표시할 쿠폰이 없습니다.
                  </p>

                </div>

              ) : (

                <div
                  className="
                    mt-5
                    grid
                    grid-cols-1
                    gap-3
                    lg:grid-cols-2
                  "
                >

                  {filteredUserCoupons.map(
                    (coupon) => {

                      const processing =
                        couponProcessingId ===
                        coupon.userCouponId;


                      const rewardStoreId =
                        coupon.rewardMode ===
                          'RANDOM' &&
                        coupon.status ===
                          'USED'
                          ? coupon
                              .selectedRewardStoreId
                          : coupon.storeId;


                      const rewardStoreName =
                        rewardStoreId ==
                          null
                          ? undefined
                          : storeNameMap.get(
                              rewardStoreId,
                            );


                      return (
                        <article
                          key={
                            coupon.userCouponId
                          }
                          className="
                            overflow-hidden
                            rounded-xl
                            border
                            border-gray-200
                            bg-white
                          "
                        >

                          <div
                            className="
                              flex
                              items-start
                              justify-between
                              gap-3
                              px-4
                              py-3.5
                            "
                          >

                            <div
                              className="
                                min-w-0
                              "
                            >

                              <div
                                className="
                                  flex
                                  items-center
                                  gap-1.5
                                "
                              >

                                {coupon.rewardMode ===
                                'RANDOM' ? (

                                  <Sparkles
                                    size={15}
                                    className="
                                      shrink-0
                                      text-main
                                    "
                                  />

                                ) : (

                                  <Ticket
                                    size={15}
                                    className="
                                      shrink-0
                                      text-main
                                    "
                                  />

                                )}


                                <h3
                                  className="
                                    truncate
                                    text-sm
                                    font-bold
                                    text-gray-900
                                  "
                                >
                                  {coupon.couponName}
                                </h3>

                              </div>


                              {coupon.description && (
                                <p
                                  className="
                                    mt-1
                                    truncate
                                    text-xs
                                    text-gray-500
                                  "
                                >
                                  {coupon.description}
                                </p>
                              )}

                            </div>


                            <span
                              className={`
                                shrink-0
                                rounded-full
                                px-2
                                py-0.5
                                text-[10px]
                                font-bold

                                ${getCouponStatusClassName(
                                  coupon.status,
                                )}
                              `}
                            >
                              {getCouponStatusLabel(
                                coupon.status,
                              )}
                            </span>

                          </div>


                          <div
                            className="
                              border-t
                              border-gray-100
                              px-4
                              py-3
                            "
                          >

                            <div
                              className="
                                flex
                                items-center
                                gap-2
                              "
                            >

                              <Gift
                                size={15}
                                className="
                                  text-main
                                "
                              />


                              <strong
                                className="
                                  text-sm
                                  text-gray-900
                                "
                              >
                                {getUserCouponRewardLabel(
                                  coupon,
                                )}
                              </strong>

                            </div>


                            {coupon.rewardMode ===
                              'RANDOM' &&
                              coupon.status ===
                                'USED' && (

                              <p
                                className="
                                  mt-1
                                  pl-6
                                  text-[11px]
                                  font-semibold
                                  text-main
                                "
                              >
                                랜덤 당첨 결과
                              </p>

                            )}


                            <div
                              className="
                                mt-3
                                flex
                                flex-wrap
                                gap-x-4
                                gap-y-1
                                border-t
                                border-gray-100
                                pt-2.5
                                text-[11px]
                                text-gray-500
                              "
                            >

                              {rewardStoreName && (
                                <span>
                                  {rewardStoreName}
                                </span>
                              )}


                              <span>
                                만료{' '}
                                {formatSimpleDate(
                                  coupon.expiresAt,
                                )}
                              </span>


                              {coupon.status ===
                                'USED' && (

                                <span>
                                  사용{' '}
                                  {formatDateTime(
                                    coupon.usedAt,
                                  )}
                                </span>

                              )}

                            </div>


                            {coupon.memo && (
                              <div
                                className="
                                  mt-2
                                  rounded-lg
                                  bg-gray-50
                                  px-3
                                  py-2
                                  text-xs
                                  text-gray-500
                                "
                              >
                                메모: {coupon.memo}
                              </div>
                            )}

                          </div>


                          {coupon.status ===
                            'AVAILABLE' && (

                            <div
                              className="
                                border-t
                                border-gray-100
                                bg-gray-50/50
                                px-3
                                py-2.5
                              "
                            >

                              {coupon.rewardMode ===
                              'RANDOM' ? (

                                <div
                                  className="
                                    flex
                                    items-center
                                    justify-between
                                    gap-3
                                  "
                                >

                                  <p
                                    className="
                                      text-xs
                                      font-medium
                                      text-gray-500
                                    "
                                  >
                                    랜덤 쿠폰은 회원이 직접 사용합니다.
                                  </p>


                                  <button
                                    type="button"
                                    disabled={
                                      processing
                                    }
                                    onClick={() =>
                                      void handleCancelCoupon(
                                        coupon,
                                      )
                                    }
                                    className="
                                      h-9
                                      shrink-0
                                      rounded-lg
                                      border
                                      border-red-200
                                      bg-white
                                      px-4
                                      text-xs
                                      font-bold
                                      text-red-500
                                      disabled:opacity-50
                                    "
                                  >
                                    {processing
                                      ? '처리 중...'
                                      : '취소'}
                                  </button>

                                </div>

                              ) : (

                                <div
                                  className="
                                    grid
                                    grid-cols-2
                                    gap-2
                                  "
                                >

                                  <button
                                    type="button"
                                    disabled={
                                      processing
                                    }
                                    onClick={() =>
                                      void handleCancelCoupon(
                                        coupon,
                                      )
                                    }
                                    className="
                                      h-9
                                      rounded-lg
                                      border
                                      border-red-200
                                      bg-white
                                      text-xs
                                      font-bold
                                      text-red-500
                                      disabled:opacity-50
                                    "
                                  >
                                    {processing
                                      ? '처리 중...'
                                      : '취소'}
                                  </button>


                                  <button
                                    type="button"
                                    disabled={
                                      processing
                                    }
                                    onClick={() =>
                                      void handleUseCoupon(
                                        coupon,
                                      )
                                    }
                                    className="
                                      h-9
                                      rounded-lg
                                      bg-main
                                      text-xs
                                      font-bold
                                      text-white
                                      disabled:opacity-50
                                    "
                                  >
                                    {processing
                                      ? '처리 중...'
                                      : '사용 처리'}
                                  </button>

                                </div>

                              )}

                            </div>

                          )}

                        </article>
                      );
                    },
                  )}

                </div>

              )}

            </div>

          )}

        </section>


        {/* =====================================================
            포인트 / 스코어 내역
            쿠폰 탭에서는 숨김
            ===================================================== */}
        {activeTab !==
          'coupon' && (
          <>

            <section
              className="
                mb-3
                rounded-2xl
                border
                border-gray-200
                bg-white
                p-4
                shadow-sm
                sm:p-5
              "
            >

              <div
                className="
                  flex
                  flex-col
                  gap-4
                  lg:flex-row
                  lg:items-center
                  lg:justify-between
                "
              >

                <div
                  className="
                    flex
                    items-center
                    gap-2
                  "
                >

                  <History
                    size={18}
                    className="
                      text-gray-500
                    "
                  />


                  <div>

                    <h2
                      className="
                        text-base
                        font-bold
                        text-gray-900
                      "
                    >
                      {activeTab ===
                        'score'
                        ? '스코어 내역'
                        : `${pointLabel} 내역`}
                    </h2>


                    <p
                      className="
                        mt-0.5
                        text-xs
                        text-gray-400
                      "
                    >
                      총 {totalCount.toLocaleString(
                        'ko-KR',
                      )}건
                    </p>

                  </div>

                </div>


                <div
                  className="
                    flex
                    flex-col
                    gap-2
                    sm:flex-row
                    sm:items-center
                  "
                >

                  <div
                    className="
                      flex
                      items-center
                      gap-1.5
                      text-xs
                      font-semibold
                      text-gray-500
                    "
                  >
                    <CalendarDays
                      size={15}
                    />

                    조회 기간
                  </div>


                  <div
                    className="
                      grid
                      grid-cols-[1fr_1fr_auto_1fr_1fr]
                      items-center
                      gap-1.5
                    "
                  >

                    <select
                      value={
                        startYear
                      }
                      onChange={(
                        event,
                      ) => {

                        setStartYear(
                          Number(
                            event.target.value,
                          ),
                        );

                        setCurrentPage(
                          1,
                        );

                      }}
                      className="
                        h-9
                        rounded-lg
                        border
                        border-gray-200
                        bg-gray-50
                        px-2
                        text-xs
                      "
                    >
                      {yearOptions.map(
                        (year) => (
                          <option
                            key={
                              year
                            }
                            value={
                              year
                            }
                          >
                            {year}년
                          </option>
                        ),
                      )}
                    </select>


                    <select
                      value={
                        startMonth
                      }
                      onChange={(
                        event,
                      ) => {

                        setStartMonth(
                          Number(
                            event.target.value,
                          ),
                        );

                        setCurrentPage(
                          1,
                        );

                      }}
                      className="
                        h-9
                        rounded-lg
                        border
                        border-gray-200
                        bg-gray-50
                        px-2
                        text-xs
                      "
                    >
                      {monthOptions.map(
                        (month) => (
                          <option
                            key={
                              month
                            }
                            value={
                              month
                            }
                          >
                            {month}월
                          </option>
                        ),
                      )}
                    </select>


                    <span
                      className="
                        px-1
                        text-xs
                        text-gray-400
                      "
                    >
                      ~
                    </span>


                    <select
                      value={
                        endYear
                      }
                      onChange={(
                        event,
                      ) => {

                        setEndYear(
                          Number(
                            event.target.value,
                          ),
                        );

                        setCurrentPage(
                          1,
                        );

                      }}
                      className="
                        h-9
                        rounded-lg
                        border
                        border-gray-200
                        bg-gray-50
                        px-2
                        text-xs
                      "
                    >
                      {yearOptions.map(
                        (year) => (
                          <option
                            key={
                              year
                            }
                            value={
                              year
                            }
                          >
                            {year}년
                          </option>
                        ),
                      )}
                    </select>


                    <select
                      value={
                        endMonth
                      }
                      onChange={(
                        event,
                      ) => {

                        setEndMonth(
                          Number(
                            event.target.value,
                          ),
                        );

                        setCurrentPage(
                          1,
                        );

                      }}
                      className="
                        h-9
                        rounded-lg
                        border
                        border-gray-200
                        bg-gray-50
                        px-2
                        text-xs
                      "
                    >
                      {monthOptions.map(
                        (month) => (
                          <option
                            key={
                              month
                            }
                            value={
                              month
                            }
                          >
                            {month}월
                          </option>
                        ),
                      )}
                    </select>

                  </div>

                </div>

              </div>

            </section>


            {/* Mobile History */}
            <section
              className="
                md:hidden
              "
            >

              {currentData.length ===
              0 ? (

                <div
                  className="
                    flex
                    min-h-48
                    items-center
                    justify-center
                    rounded-2xl
                    border
                    border-gray-200
                    bg-white
                    p-6
                    text-sm
                    text-gray-400
                  "
                >
                  {activeTab ===
                    'score'
                    ? '스코어'
                    : pointLabel}{' '}
                  내역이 없습니다.
                </div>

              ) : (

                <div
                  className="
                    space-y-2
                  "
                >

                  {currentData.map(
                    (item) => {

                      const delta =
                        getHistoryDelta(
                          item,
                        );


                      return (
                        <article
                          key={
                            item.historyId
                          }
                          className="
                            overflow-hidden
                            rounded-xl
                            border
                            border-gray-200
                            bg-white
                            shadow-sm
                          "
                        >

                          <div
                            className="
                              flex
                              items-center
                              justify-between
                              gap-3
                              px-4
                              py-3
                            "
                          >

                            <div>

                              <p
                                className="
                                  text-xs
                                  text-gray-400
                                "
                              >
                                {new Date(
                                  item.createdAt,
                                ).toLocaleString(
                                  'ko-KR',
                                )}
                              </p>


                              <p
                                className="
                                  mt-1
                                  text-sm
                                  font-semibold
                                  text-gray-700
                                "
                              >
                                {getActorDisplay(
                                  item,
                                )}
                              </p>

                            </div>


                            <strong
                              className={`
                                text-base
                                font-bold

                                ${
                                  delta >
                                  0
                                    ? 'text-emerald-600'
                                    : delta <
                                        0
                                      ? 'text-red-600'
                                      : 'text-gray-500'
                                }
                              `}
                            >
                              {delta >
                              0
                                ? '+'
                                : ''}

                              {delta.toLocaleString(
                                'ko-KR',
                              )}
                            </strong>

                          </div>


                          <div
                            className="
                              border-t
                              border-gray-100
                              px-4
                              py-2
                            "
                          >

                            <button
                              type="button"
                              onClick={() =>
                                openMemoEditor(
                                  item,
                                )
                              }
                              className="
                                flex
                                w-full
                                items-center
                                justify-between
                                py-1.5
                                text-xs
                                text-gray-500
                              "
                            >

                              <span>
                                메모
                              </span>


                              <span
                                className="
                                  flex
                                  items-center
                                  gap-1.5
                                "
                              >
                                {!item.memo?.trim() && (
                                  <Pencil
                                    size={13}
                                  />
                                )}

                                {item.memo?.trim()
                                  ? getMemoPreview(
                                      item.memo,
                                    )
                                  : '작성하기'}
                              </span>

                            </button>

                          </div>

                        </article>
                      );

                    },
                  )}

                </div>

              )}

            </section>


            {/* Desktop History */}
            <section
              className="
                hidden
                overflow-hidden
                rounded-2xl
                border
                border-gray-200
                bg-white
                shadow-sm
                md:block
              "
            >

              <div
                className="
                  overflow-x-auto
                "
              >

                <table
                  className="
                    w-full
                    min-w-[760px]
                  "
                >

                  <thead
                    className="
                      bg-gray-50
                    "
                  >
                    <tr>

                      <th
                        className="
                          px-5
                          py-3
                          text-left
                          text-xs
                          font-semibold
                          text-gray-500
                        "
                      >
                        날짜
                      </th>

                      <th
                        className="
                          px-5
                          py-3
                          text-left
                          text-xs
                          font-semibold
                          text-gray-500
                        "
                      >
                        처리자
                      </th>

                      <th
                        className="
                          px-5
                          py-3
                          text-right
                          text-xs
                          font-semibold
                          text-gray-500
                        "
                      >
                        {activeTab ===
                          'score'
                          ? 'Score'
                          : 'Point'}
                      </th>

                      <th
                        className="
                          px-5
                          py-3
                          text-center
                          text-xs
                          font-semibold
                          text-gray-500
                        "
                      >
                        메모
                      </th>

                    </tr>
                  </thead>


                  <tbody>

                    {currentData.length ===
                    0 ? (

                      <tr>

                        <td
                          colSpan={4}
                          className="
                            px-6
                            py-16
                            text-center
                            text-sm
                            text-gray-400
                          "
                        >
                          내역이 없습니다.
                        </td>

                      </tr>

                    ) : (

                      currentData.map(
                        (item) => {

                          const delta =
                            getHistoryDelta(
                              item,
                            );


                          return (
                            <tr
                              key={
                                item.historyId
                              }
                              className="
                                border-t
                                border-gray-100
                                hover:bg-gray-50
                              "
                            >

                              <td
                                className="
                                  whitespace-nowrap
                                  px-5
                                  py-3.5
                                  text-sm
                                  text-gray-600
                                "
                              >
                                {new Date(
                                  item.createdAt,
                                ).toLocaleString(
                                  'ko-KR',
                                )}
                              </td>

                              <td
                                className="
                                  px-5
                                  py-3.5
                                  text-sm
                                  font-medium
                                  text-gray-700
                                "
                              >
                                {getActorDisplay(
                                  item,
                                )}
                              </td>

                              <td
                                className={`
                                  px-5
                                  py-3.5
                                  text-right
                                  text-sm
                                  font-bold

                                  ${
                                    delta >
                                    0
                                      ? 'text-emerald-600'
                                      : delta <
                                          0
                                        ? 'text-red-600'
                                        : 'text-gray-500'
                                  }
                                `}
                              >
                                {delta >
                                0
                                  ? '+'
                                  : ''}

                                {delta.toLocaleString(
                                  'ko-KR',
                                )}
                              </td>

                              <td
                                className="
                                  px-5
                                  py-3.5
                                  text-center
                                "
                              >

                                <button
                                  type="button"
                                  onClick={() =>
                                    openMemoEditor(
                                      item,
                                    )
                                  }
                                  className="
                                    inline-flex
                                    max-w-[220px]
                                    items-center
                                    gap-1.5
                                    rounded-lg
                                    px-3
                                    py-1.5
                                    text-xs
                                    font-medium
                                    text-gray-500
                                    hover:bg-gray-100
                                  "
                                >

                                  {!item.memo?.trim() && (
                                    <Pencil
                                      size={14}
                                    />
                                  )}


                                  <span
                                    className="
                                      truncate
                                    "
                                  >
                                    {item.memo?.trim()
                                      ? getMemoPreview(
                                          item.memo,
                                        )
                                      : '메모 작성'}
                                  </span>

                                </button>

                              </td>

                            </tr>
                          );

                        },
                      )

                    )}

                  </tbody>

                </table>

              </div>

            </section>


            {totalCount >
              0 && (

              <footer
                className="
                  mt-5
                  flex
                  justify-center
                "
              >
                <Pagination
                  currentPage={
                    currentPage
                  }
                  totalItems={
                    totalCount
                  }
                  itemsPerPage={
                    ITEMS_PER_PAGE
                  }
                  onPageChange={
                    setCurrentPage
                  }
                />
              </footer>

            )}

          </>
        )}

      </div>


      {/* =====================================================
          쿠폰 발급 모달
          ===================================================== */}
      {issueCouponModalOpen && (

        <div
          className="
            fixed
            inset-0
            z-50
            flex
            items-end
            justify-center
            bg-black/50
            sm:items-center
            sm:p-4
          "
          onMouseDown={(
            event,
          ) => {

            if (
              event.target ===
              event.currentTarget &&
              !issuingCoupon
            ) {

              setIssueCouponModalOpen(
                false,
              );

            }

          }}
        >

          <section
            className="
              flex
              max-h-[90vh]
              w-full
              flex-col
              overflow-hidden
              rounded-t-2xl
              bg-white
              shadow-2xl
              sm:max-w-lg
              sm:rounded-2xl
            "
          >

            <header
              className="
                flex
                items-center
                justify-between
                border-b
                border-gray-100
                px-5
                py-4
              "
            >

              <div>

                <h3
                  className="
                    text-base
                    font-bold
                    text-gray-900
                  "
                >
                  쿠폰 발급
                </h3>


                <p
                  className="
                    mt-0.5
                    text-xs
                    text-gray-500
                  "
                >
                  {user.userName}
                  {' · '}
                  {user.userNickname}
                </p>

              </div>


              <button
                type="button"
                disabled={
                  issuingCoupon
                }
                onClick={() =>
                  setIssueCouponModalOpen(
                    false,
                  )
                }
                className="
                  grid
                  h-9
                  w-9
                  place-items-center
                  rounded-lg
                  text-gray-400
                  hover:bg-gray-100
                "
              >
                <X
                  size={19}
                />
              </button>

            </header>


            <div
              className="
                min-h-0
                flex-1
                overflow-y-auto
                p-5
              "
            >

              <label
                className="
                  text-sm
                  font-bold
                  text-gray-700
                "
              >
                발급할 쿠폰
              </label>


              {availableCouponTemplates.length ===
                0 ? (

                <div
                  className="
                    mt-2
                    rounded-xl
                    bg-gray-50
                    px-4
                    py-8
                    text-center
                    text-sm
                    text-gray-500
                  "
                >
                  발급 가능한 활성 쿠폰이 없습니다.
                </div>

              ) : (

                <div
                  className="
                    mt-2
                    space-y-2
                  "
                >

                  {availableCouponTemplates.map(
                    (coupon) => {

                      const selected =
                        selectedIssueCouponId ===
                        coupon.couponId;


                      const storeName =
                        coupon.storeId ==
                          null
                          ? '공용'
                          : storeNameMap.get(
                              coupon.storeId,
                            ) ??
                            `지점 #${coupon.storeId}`;


                      return (
                        <button
                          key={
                            coupon.couponId
                          }
                          type="button"
                          onClick={() =>
                            setSelectedIssueCouponId(
                              coupon.couponId,
                            )
                          }
                          className={`
                            flex
                            w-full
                            items-center
                            gap-3
                            rounded-xl
                            border
                            px-3
                            py-3
                            text-left

                            ${
                              selected
                                ? `
                                  border-main
                                  bg-main/5
                                `
                                : `
                                  border-gray-200
                                  bg-white
                                `
                            }
                          `}
                        >

                          <div
                            className="
                              flex
                              h-9
                              w-9
                              shrink-0
                              items-center
                              justify-center
                              rounded-lg
                              bg-gray-50
                              text-main
                            "
                          >

                            {coupon.rewardMode ===
                            'RANDOM' ? (

                              <Sparkles
                                size={17}
                              />

                            ) : (

                              <Ticket
                                size={17}
                              />

                            )}

                          </div>


                          <div
                            className="
                              min-w-0
                              flex-1
                            "
                          >

                            <p
                              className="
                                truncate
                                text-sm
                                font-bold
                                text-gray-900
                              "
                            >
                              {coupon.couponName}
                            </p>


                            <p
                              className="
                                mt-0.5
                                truncate
                                text-xs
                                text-gray-500
                              "
                            >
                              {getCouponTemplateRewardLabel(
                                coupon,
                              )}

                              {' · '}

                              {storeName}

                              {' · '}

                              {coupon.validDays}일
                            </p>

                          </div>

                        </button>
                      );

                    },
                  )}

                </div>

              )}


              <label
                className="
                  mt-5
                  block
                  text-sm
                  font-bold
                  text-gray-700
                "
              >
                메모

                <span
                  className="
                    ml-1
                    text-xs
                    font-normal
                    text-gray-400
                  "
                >
                  선택
                </span>
              </label>


              <textarea
                value={
                  issueMemo
                }
                onChange={(
                  event,
                ) =>
                  setIssueMemo(
                    event.target.value,
                  )
                }
                rows={3}
                placeholder="예: 신규회원 이벤트"
                className="
                  mt-2
                  w-full
                  resize-none
                  rounded-xl
                  border
                  border-gray-300
                  p-3
                  text-sm
                  outline-none
                  focus:border-main
                "
              />

            </div>


            <footer
              className="
                grid
                grid-cols-2
                gap-2
                border-t
                border-gray-100
                bg-gray-50
                p-4
              "
            >

              <button
                type="button"
                disabled={
                  issuingCoupon
                }
                onClick={() =>
                  setIssueCouponModalOpen(
                    false,
                  )
                }
                className="
                  h-11
                  rounded-xl
                  border
                  border-gray-200
                  bg-white
                  text-sm
                  font-semibold
                  text-gray-600
                "
              >
                취소
              </button>


              <button
                type="button"
                disabled={
                  issuingCoupon ||
                  selectedIssueCouponId ==
                    null
                }
                onClick={() =>
                  void handleIssueCoupon()
                }
                className="
                  h-11
                  rounded-xl
                  bg-main
                  text-sm
                  font-bold
                  text-white
                  disabled:opacity-50
                "
              >
                {issuingCoupon
                  ? '발급 중...'
                  : '쿠폰 발급'}
              </button>

            </footer>

          </section>

        </div>

      )}


      {/* =====================================================
          Memo Modal
          ===================================================== */}
      {memoEditingItem && (

        <div
          className="
            fixed
            inset-0
            z-50
            flex
            items-end
            justify-center
            bg-black/50
            sm:items-center
            sm:p-4
          "
          role="dialog"
          aria-modal="true"
          onMouseDown={(
            event,
          ) => {

            if (
              event.target ===
              event.currentTarget
            ) {

              closeMemoEditor();

            }

          }}
        >

          <section
            className="
              w-full
              rounded-t-2xl
              bg-white
              shadow-2xl
              sm:max-w-lg
              sm:rounded-2xl
            "
          >

            <header
              className="
                flex
                items-center
                justify-between
                border-b
                border-gray-100
                px-5
                py-4
              "
            >

              <div>

                <h3
                  className="
                    text-base
                    font-bold
                    text-gray-900
                  "
                >
                  {memoEditingItem.memo?.trim()
                    ? '메모 수정'
                    : '메모 작성'}
                </h3>


                <p
                  className="
                    mt-0.5
                    text-xs
                    text-gray-400
                  "
                >
                  내역 #
                  {
                    memoEditingItem.historyId
                  }
                </p>

              </div>


              <button
                type="button"
                onClick={
                  closeMemoEditor
                }
                disabled={
                  isMemoSaving
                }
                className="
                  grid
                  h-9
                  w-9
                  place-items-center
                  rounded-lg
                  text-gray-400
                  hover:bg-gray-100
                  disabled:opacity-50
                "
              >
                <X
                  size={19}
                />
              </button>

            </header>


            <div
              className="
                p-5
              "
            >

              <textarea
                value={
                  memoInput
                }
                onChange={(
                  event,
                ) =>
                  setMemoInput(
                    event.target.value,
                  )
                }
                rows={7}
                maxLength={500}
                disabled={
                  isMemoSaving
                }
                placeholder="이 내역에 대한 메모를 입력해주세요."
                className="
                  w-full
                  resize-none
                  rounded-xl
                  border
                  border-gray-300
                  p-4
                  text-sm
                  outline-none
                  focus:border-main
                  focus:ring-2
                  focus:ring-main/10
                "
                autoFocus
              />


              <div
                className="
                  mt-2
                  flex
                  justify-between
                  text-xs
                  text-gray-400
                "
              >
                <span>
                  최대 500자
                </span>

                <span>
                  {memoInput.length}/500
                </span>
              </div>

            </div>


            <footer
              className="
                grid
                grid-cols-2
                gap-2
                border-t
                border-gray-100
                bg-gray-50
                p-4
              "
            >

              <button
                type="button"
                onClick={
                  closeMemoEditor
                }
                disabled={
                  isMemoSaving
                }
                className="
                  h-11
                  rounded-xl
                  border
                  border-gray-200
                  bg-white
                  text-sm
                  font-semibold
                  text-gray-600
                "
              >
                취소
              </button>


              <button
                type="button"
                onClick={() =>
                  void handleSaveMemo()
                }
                disabled={
                  isMemoSaving ||
                  !memoInput.trim()
                }
                className="
                  h-11
                  rounded-xl
                  bg-main
                  text-sm
                  font-bold
                  text-white
                  disabled:opacity-50
                "
              >
                {isMemoSaving
                  ? '저장 중...'
                  : '저장'}
              </button>

            </footer>

          </section>

        </div>

      )}

    </div>
  );
};


export default AdminMemberDetailPage;